import { Button, Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress, Typography } from '@mui/material';
import {
  Form,
  SelectInput,
  TextInput,
  useNotify,
  useRefresh,
  required,
  RaRecord,
  useDataProvider,
  useTranslate
} from 'react-admin';
import { useMutation } from 'react-query';
import { ORDER_DISAPPROVAL_REASON_CODE } from '../../constants/choices';

// --- 承認ダイアログ ---
interface ApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  selectedId: RaRecord['id'];
}

/**
 * 注文を承認するためのシンプルな確認ダイアログコンポーネント。
 */
export const ApprovalDialog = ({ open, onClose, selectedId }: ApprovalDialogProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const { mutate, isLoading } = useMutation(
    // 承認APIを呼び出す (リクエストボディは空)
    () => dataProvider.create(`orders/${encodeURIComponent(selectedId)}/approve`, { data: {} }),
    {
      onSuccess: () => {
        notify('注文を承認しました。', { type: 'success' });
        refresh();
        onClose();
      },
      onError: (error: any) => {
        notify(`エラー: ${error.message || '注文の承認に失敗しました。'}`, { type: 'error' });
        onClose();
      }
    }
  );

  const handleConfirm = () => {
    mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="approval-dialog-title">
      <DialogTitle id="approval-dialog-title">注文の承認</DialogTitle>
      <DialogContent>
        <Typography>
          選択した注文を承認しますか？
          <br />
          この処理は取り消しできません。
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          キャンセル
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={isLoading}>
          {isLoading && <CircularProgress size={20} sx={{ marginRight: 1 }} />}
          承認する
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- 否認ダイアログ ---

interface DisapprovalDialogProps {
  open: boolean;
  onClose: () => void;
  selectedIds: RaRecord['id'][];
  record?: RaRecord;
  readOnly?: boolean;
}

/**
 * 注文を否認するためのダイアログコンポーネント。
 * 否認理由の選択と入力を求める。
 */
export const DisapprovalDialog = ({ open, onClose, selectedIds, record, readOnly = false }: DisapprovalDialogProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const dataProvider = useDataProvider();

  const { mutate, isLoading } = useMutation<any, any, { disapprovalReasonCode: number; disapprovalReason: string }>(
    (params) => {
      const id = selectedIds[0];
      const payload = { data: params };
      if (!id) {
        return Promise.reject(new Error('対象のIDが見つかりません。'));
      }
      return dataProvider.create(`orders/${encodeURIComponent(id)}/disapprove`, payload);
    },
    {
      onSuccess: () => {
        notify('注文を否認しました。', { type: 'info' });
        refresh();
        onClose();
      },
      onError: (error: any) => {
        let errorMessage = error.message || '注文の否認に失敗しました。';
        if (error.body && error.body.errors) {
          const errorKey = Object.keys(error.body.errors)[0];
          if (errorKey) {
            errorMessage = error.body.errors[errorKey];
          }
        }
        notify(`エラー: ${errorMessage}`, { type: 'error' });
      }
    }
  );

  const handleSubmit = (values: any) => {
    if (readOnly) return;
    mutate({
      disapprovalReasonCode: values.disapprovalReasonCode,
      disapprovalReason: values.disapprovalReason || ''
    });
  };
  const initialValues = readOnly ? record : {};

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="disapproval-dialog-title">
      <Form onSubmit={handleSubmit} defaultValues={initialValues}>
        <DialogTitle id="disapproval-dialog-title">注文の否認</DialogTitle>
        <DialogContent>
          <Typography>
            選択した注文を否認します。
            <br />
            この処理は取り消しできません。
          </Typography>
          <SelectInput
            source="disapprovalReasonCode"
            label={translate('resources.orders.fields.disapprovalReasonCode')}
            choices={ORDER_DISAPPROVAL_REASON_CODE}
            optionText="name"
            optionValue="id"
            validate={!readOnly ? required('この項目は必須です') : undefined} // readOnly時はバリデーション不要
            fullWidth
            disabled={readOnly}
          />
          <TextInput
            source="disapprovalReason"
            label={translate('resources.orders.fields.disapprovalReason')}
            multiline
            rows={3}
            fullWidth
            helperText={!readOnly ? '否認理由コードで[その他]を選択した場合は、詳細を記述してください。' : ''}
            disabled={readOnly}
            sx={{
              '& .MuiInputLabel-root': {
                top: '-6px' // ラベルの位置微調整
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            キャンセル
          </Button>
          {!readOnly && (
            <Button type="submit" variant="contained" color="warning" disabled={isLoading}>
              {isLoading && <CircularProgress size={20} sx={{ marginRight: 1 }} />}
              否認する
            </Button>
          )}
        </DialogActions>
      </Form>
    </Dialog>
  );
};
